import React, { useEffect, useState } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc, serverTimestamp, query, where} from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './AuthContext';
import { format } from 'date-fns';

const TIPOS_VENTA = ['Embajadora', 'Tienda nube'];
const METODOS_ENTREGA = ['En mano', 'Envío', 'Retiro en local'];

export default function Ventas() {
  const { userData } = useAuth();
  const isAdmin = userData?.role === 'admin';
  const [ventas, setVentas] = useState([]);
  const [stock, setStock] = useState([]);
  const [embajadoras, setEmbajadoras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filterEmb, setFilterEmb] = useState('all');
  const [search, setSearch] = useState('');
  const emptyForm = {
    embajadoraId: '', stockId: '', cliente: '', cantidad: 1,
    tipoVenta: 'Embajadora', metodoPago: '', metodoEntrega: '', precio: '', observacion: ''
  };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!userData) return;
    load();
  }, [userData]);

  async function load() {
    if (!userData?.uid) return;
    setLoading(true);
    try {
      // Embajadoras (solo admin las necesita para el filtro)
      if (isAdmin) {
        const usersSnap = await getDocs(query(collection(db, 'users'), where('approved', '==', true)));
        const embs = [];
        usersSnap.forEach(d => embs.push({ id: d.id, ...d.data() }));
        setEmbajadoras(embs);
      }

      // Stock — cada una ve el suyo
      const stockQ = isAdmin
        ? collection(db, 'stock')
        : query(collection(db, 'stock'), where('embajadoraId', '==', userData.uid));
      const stockSnap = await getDocs(stockQ);
      const stockRows = [];
      stockSnap.forEach(d => stockRows.push({ id: d.id, ...d.data() }));
      setStock(stockRows);

      // Ventas sin orderBy — evita conflicto con serverTimestamp recién creado
      const ventasSnap = await getDocs(collection(db, 'ventas'));
      const rows = [];
      ventasSnap.forEach(d => {
        const v = { id: d.id, ...d.data() };
        if (!isAdmin && v.embajadoraId !== userData.uid) return;
        rows.push(v);
      });
      // Ordenar en cliente
      rows.sort((a, b) => {
        const fa = a.fecha?.toDate ? a.fecha.toDate() : new Date(a.fecha || 0);
        const fb = b.fecha?.toDate ? b.fecha.toDate() : new Date(b.fecha || 0);
        return fb - fa;
      });
      setVentas(rows);
    } catch (err) {
      console.error('Error cargando ventas:', err);
    }
    setLoading(false);
  }

  const embStock = stock.filter(s =>
    isAdmin
      ? (form.embajadoraId ? s.embajadoraId === form.embajadoraId : true)
      : s.embajadoraId === userData?.uid
  );

  const selectedStock = stock.find(s => s.id === form.stockId);
  const cantidadVenta = Number(form.cantidad) || 1;
  const precioUnitario = Number(form.precio) || selectedStock?.precio || 0;
  const precioTotal = precioUnitario * cantidadVenta;

  async function handleVenta(e) {
    e.preventDefault();
    if (!userData?.uid) return;
    setError('');
    const stk = stock.find(s => s.id === form.stockId);
    if (!stk) { setError('Seleccioná un producto.'); return; }
    if (stk.stockDisponible < cantidadVenta) {
      setError(`Stock insuficiente. Disponible: ${stk.stockDisponible}`);
      return;
    }
    setSaving(true);
    try {
      const embId = isAdmin ? form.embajadoraId : userData.uid;
      const embNombre = isAdmin
        ? (embajadoras.find(e => e.id === embId)?.name || '')
        : userData.name;
      const comision = precioTotal * 0.20;
      const importeMalaika = precioTotal - comision;

      await addDoc(collection(db, 'ventas'), {
        embajadoraId: embId,
        embajadoraNombre: embNombre,
        stockId: stk.id,
        producto: stk.producto,
        aroma: stk.aroma,
        cliente: form.cliente,
        cantidad: cantidadVenta,
        precioUnitario,
        precio: precioTotal,
        comision,
        importeMalaika,
        tipoVenta: form.tipoVenta,
        metodoPago: form.metodoPago,
        metodoEntrega: form.metodoEntrega,
        observacion: form.observacion,
        fecha: serverTimestamp()
      });

      // Descontar del stock
      await updateDoc(doc(db, 'stock', stk.id), {
        egresos: (stk.egresos || 0) + cantidadVenta,
        stockDisponible: stk.stockDisponible - cantidadVenta,
        updatedAt: serverTimestamp()
      });

      // Registrar movimiento
      await addDoc(collection(db, 'movimientos'), {
        tipo: 'venta',
        stockId: stk.id,
        producto: stk.producto,
        aroma: stk.aroma,
        embajadoraId: embId,
        embajadoraNombre: embNombre,
        cantidad: -cantidadVenta,
        observacion: `Venta${form.cliente ? ` a ${form.cliente}` : ''} (x${cantidadVenta})`,
        fecha: serverTimestamp()
      });

      await load();
      setShowModal(false);
      setForm(emptyForm);
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  }

  const filtered = ventas.filter(v => {
    if (filterEmb !== 'all' && v.embajadoraId !== filterEmb) return false;
    if (search && !`${v.producto} ${v.cliente || ''} ${v.embajadoraNombre || ''}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totalFiltrado = filtered.reduce((s, v) => s + (v.precio || 0), 0);
  const comisionFiltrada = filtered.reduce((s, v) => s + (v.comision || 0), 0);

  return (
    <div>
      <div className="page-header">
        <h1>{isAdmin ? 'Ventas' : 'Mis Ventas'}</h1>
        <p>Registro de ventas y comisiones</p>
      </div>
      <div className="page-body">
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          <div className="stat-card">
            <div className="stat-label">Total ventas</div>
            <div className="stat-value">${totalFiltrado.toLocaleString('es-AR', { maximumFractionDigits: 0 })}</div>
            <div className="stat-sub">{filtered.length} operaciones</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">{isAdmin ? 'Total comisiones' : 'Mi comisión'}</div>
            <div className="stat-value">${comisionFiltrada.toLocaleString('es-AR', { maximumFractionDigits: 0 })}</div>
          </div>
          {isAdmin && (
            <div className="stat-card">
              <div className="stat-label">Para Floreciendo</div>
              <div className="stat-value">${(totalFiltrado - comisionFiltrada).toLocaleString('es-AR', { maximumFractionDigits: 0 })}</div>
            </div>
          )}
        </div>

        <div className="toolbar">
          <div className="toolbar-left">
            <input className="search-input" placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} />
            {isAdmin && (
              <select value={filterEmb} onChange={e => setFilterEmb(e.target.value)}>
                <option value="all">Todas las embajadoras</option>
                {embajadoras.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            )}
          </div>
          <button className="btn btn-primary" onClick={() => { setShowModal(true); setError(''); setForm(emptyForm); }}>
            + Nueva venta
          </button>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            {loading ? <div className="spinner" /> : (
              <table>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    {isAdmin && <th>Embajadora</th>}
                    <th>Producto</th><th>Aroma</th><th>Cliente</th>
                    <th>Cant.</th><th>P. Unit.</th><th>Total</th><th>Comisión</th>
                    {isAdmin && <th>Para Floreciendo</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={10} style={{ textAlign: 'center', color: 'var(--text-light)', padding: 40 }}>
                      Sin ventas registradas
                    </td></tr>
                  ) : filtered.map(v => (
                    <tr key={v.id}>
                      <td>{v.fecha ? format(v.fecha.toDate ? v.fecha.toDate() : new Date(v.fecha), 'dd/MM/yy') : '-'}</td>
                      {isAdmin && <td>{v.embajadoraNombre}</td>}
                      <td style={{ fontWeight: 500 }}>{v.producto}</td>
                      <td style={{ color: 'var(--text-light)' }}>{v.aroma}</td>
                      <td>{v.cliente || '-'}</td>
                      <td style={{ fontWeight: 600 }}>{v.cantidad || 1}</td>
                      <td style={{ color: 'var(--text-light)' }}>${(v.precioUnitario || v.precio || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}</td>
                      <td style={{ fontWeight: 700 }}>${(v.precio || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}</td>
                      <td style={{ color: 'var(--forest)' }}>${(v.comision || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}</td>
                      {isAdmin && <td>${(v.importeMalaika || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Registrar venta</div>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}
            <form className="modal-form" onSubmit={handleVenta}>
              {isAdmin && (
                <div className="field-group">
                  <label>Embajadora</label>
                  <select value={form.embajadoraId} onChange={e => setForm(f => ({ ...f, embajadoraId: e.target.value, stockId: '', precio: '' }))} required>
                    <option value="">Seleccionar...</option>
                    {embajadoras.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
              )}
              <div className="field-group">
                <label>Producto</label>
                <select value={form.stockId} onChange={e => {
                  const stk = stock.find(s => s.id === e.target.value);
                  setForm(f => ({ ...f, stockId: e.target.value, precio: stk?.precio || '' }));
                }} required>
                  <option value="">Seleccionar...</option>
                  {embStock.map(s => (
                    <option key={s.id} value={s.id} disabled={s.stockDisponible <= 0}>
                      {s.producto} · {s.aroma} — ${Number(s.precio || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })} (Stock: {s.stockDisponible})
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid-2">
                <div className="field-group">
                  <label>Cantidad</label>
                  <input type="number" min="1" value={form.cantidad} onChange={e => setForm(f => ({ ...f, cantidad: e.target.value }))} required />
                </div>
                <div className="field-group">
                  <label>Precio unitario $</label>
                  <input type="number" min="0" value={form.precio} onChange={e => setForm(f => ({ ...f, precio: e.target.value }))} required />
                </div>
              </div>
              {form.precio && form.cantidad && (
                <div className="info-pill">
                  Total: <strong>${precioTotal.toLocaleString('es-AR', { maximumFractionDigits: 0 })}</strong>
                  &nbsp;· Comisión (20%): <strong>${(precioTotal * 0.2).toLocaleString('es-AR', { maximumFractionDigits: 0 })}</strong>
                  &nbsp;· Para Floreciendo: <strong>${(precioTotal * 0.8).toLocaleString('es-AR', { maximumFractionDigits: 0 })}</strong>
                </div>
              )}
              <div className="field-group">
                <label>Cliente</label>
                <input value={form.cliente} onChange={e => setForm(f => ({ ...f, cliente: e.target.value }))} placeholder="Nombre del cliente" />
              </div>
              <div className="grid-2">
                <div className="field-group">
                  <label>Tipo de venta</label>
                  <select value={form.tipoVenta} onChange={e => setForm(f => ({ ...f, tipoVenta: e.target.value }))}>
                    {TIPOS_VENTA.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="field-group">
                  <label>Método de entrega</label>
                  <select value={form.metodoEntrega} onChange={e => setForm(f => ({ ...f, metodoEntrega: e.target.value }))}>
                    <option value="">-</option>
                    {METODOS_ENTREGA.map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div className="field-group">
                <label>Método de pago</label>
                <input value={form.metodoPago} onChange={e => setForm(f => ({ ...f, metodoPago: e.target.value }))} placeholder="Efectivo, transferencia..." />
              </div>
              <div className="field-group">
                <label>Observación</label>
                <textarea rows={2} value={form.observacion} onChange={e => setForm(f => ({ ...f, observacion: e.target.value }))} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? '...' : 'Registrar venta'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}